/**
 * AppShell.tsx — The application frame: TopNav + Sidebar + routed content +
 * right-rail AI assistant. Used as a layout route; renders <Outlet/> for the
 * active stage. Active stage, breadcrumb, and AI context all derive from the
 * current route via the nav model (lib/nav.ts); the breadcrumb project name
 * comes from the real project in the route (no fixture fallback).
 */
import { useState } from "react";
import { matchPath, Outlet, useLocation } from "react-router-dom";

import { stageFromPath } from "../../lib/nav";
import { useProject } from "../../lib/tenderApi";
import { AIAssistant } from "./AIAssistant";
import { JobsWatcher } from "./JobsWatcher";
import { Sidebar } from "./Sidebar";
import { TopNav } from "./TopNav";

export function AppShell(): JSX.Element {
  const location = useLocation();
  const stage = stageFromPath(location.pathname);

  const [sidebarVisible, setSidebarVisible] = useState(true);
  // Collapsed by default: the project assistant is an "In arrivo" panel for
  // now, so it must not claim 360px of every page.
  const [aiVisible, setAiVisible] = useState(false);

  // Project-name crumb only when the route names a real project.
  const routeProjectId =
    matchPath("/audit/:projectId", location.pathname)?.params.projectId ?? null;
  const projectQuery = useProject(routeProjectId);
  const breadcrumb = projectQuery.data ? [projectQuery.data.name, stage.label] : [stage.label];
  const showAI = !stage.hideAI;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <TopNav
        breadcrumb={breadcrumb}
        sidebarVisible={sidebarVisible}
        onToggleSidebar={() => setSidebarVisible((v) => !v)}
      />
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Sidebar active={stage.id} visible={sidebarVisible} />
        {/* Non-scrolling wrapper: the collapsed AI pill anchors here so it
            stays pinned bottom-right instead of scrolling away with content. */}
        <div style={{ flex: 1, minWidth: 0, position: "relative", display: "flex" }}>
          <main
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "auto",
              background: "var(--c-bg)",
            }}
          >
            <Outlet />
          </main>
          {showAI && !aiVisible && (
            <AIAssistant
              context={stage.aiContext}
              visible={false}
              onToggle={() => setAiVisible(true)}
            />
          )}
        </div>
        {showAI && aiVisible && (
          <AIAssistant context={stage.aiContext} visible onToggle={() => setAiVisible(false)} />
        )}
      </div>
      {/* App-wide analysis-completion notifications (toasts, bottom-right). */}
      <JobsWatcher />
    </div>
  );
}
